import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Phone, Star } from "lucide-react";
import { Leaves } from "./Leaves";
import { PHONE, PHONE_TEL, scrollToId } from "@/data/site";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
  { t: "Landscapes", cls: "font-display font-black" },
  { t: "rooted in", cls: "font-serif-i text-[var(--leaf)]" },
  { t: "Tennessee soil.", cls: "font-display font-black" },
];

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%", rotate: 3 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay: 0.25 + i * 0.13, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const HeroImage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const b = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - b.left) / b.width - 0.5);
    my.set((e.clientY - b.top) / b.height - 0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 28px 28px)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 28px 28px)" }}
      transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
      style={{ perspective: 1000 }}
      className="relative mx-auto aspect-[4/5] w-full max-w-[460px]"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="h-full w-full overflow-hidden rounded-t-full rounded-b-[28px] shadow-[0_40px_80px_-30px_rgba(15,74,19,0.55)]">
        <motion.img
          src="/img/p6.jpg"
          alt="Fresh red mulch beds and trimmed shrubs installed by HF Landscape Solutions"
          style={{ y: imgY, scale: 1.2 }}
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
    </motion.div>
  );
};

const Badge = () => (
  <div className="absolute -left-4 bottom-10 grid h-32 w-32 place-items-center rounded-full bg-white shadow-xl sm:-left-10" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 100 100" className="spin-slow absolute inset-0 h-full w-full">
      <defs><path id="c" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
      <text fontSize="8" fontWeight="700" letterSpacing="1.5" fill="#1b6b1f">
        <textPath href="#c">LOCALLY OWNED • LICENSED • INSURED •</textPath>
      </text>
    </svg>
    <img src="/img/tree.png" alt="" className="h-12 w-auto" />
  </div>
);

export const Hero = () => (
  <section id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden bg-gradient-to-b from-white via-white to-[var(--mist)] pt-28 sm:pt-32">
    <Leaves count={18} />
    <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-[1.25fr_1fr] lg:pb-28">
      <div>
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="mb-7 inline-flex items-center gap-2 rounded-full border border-[var(--leaf)]/20 bg-white/70 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] sm:text-xs sm:tracking-[0.22em] text-[var(--leaf)] backdrop-blur" data-testid="hero-eyebrow">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--leaf)]" /> Pleasant View, TN · Greater Nashville
        </motion.p>
        <h1 className="text-[clamp(3rem,7.2vw,6.4rem)] leading-[0.9] text-[var(--ink)]" data-testid="hero-title">
          {LINES.map((l, i) => <Line key={l.t} i={i} className={l.cls}>{l.t}</Line>)}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85 }} className="mt-8 max-w-xl text-base leading-relaxed text-[var(--muted)] md:text-lg">
          Grounds maintenance, retaining walls, gravel driveways and full landscaping — done with the attention to detail and the extra time every property deserves.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 1 }} className="mt-10 flex flex-wrap items-center gap-3">
          <button data-testid="hero-quote-btn" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-3 rounded-full bg-[var(--leaf)] py-4 pl-7 pr-4 font-bold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--leaf-deep)]">
            Get a free quote
            <span className="grid h-8 w-8 place-items-center rounded-full bg-white/15 transition-transform duration-300 group-hover:rotate-[-45deg]"><ArrowDownRight className="h-4 w-4" /></span>
          </button>
          <a data-testid="hero-call-btn" href={PHONE_TEL} className="inline-flex items-center gap-2 rounded-full border border-[var(--ink)]/15 bg-white/70 px-6 py-4 font-bold text-[var(--ink)] backdrop-blur transition-colors hover:border-[var(--leaf)] hover:text-[var(--leaf)]">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 1 }} className="mt-10 flex items-center gap-3 text-sm text-[var(--muted)]" data-testid="hero-rating">
          <span className="flex text-[var(--leaf)]">{[0, 1, 2, 3, 4].map((s) => <Star key={s} className="h-4 w-4 fill-current" />)}</span>
          <span><b className="text-[var(--ink)]">100% recommend</b> on Facebook</span>
        </motion.div>
      </div>
      <div className="relative">
        <HeroImage />
        <Badge />
      </div>
    </div>
  </section>
);
